import * as Sentry from '@sentry/react';
import { User } from '../types/auth';

/**
 * Set user context in Sentry for error tracking
 * This adds user information to all error reports
 * 
 * @param user - The authenticated user object, or null to clear context
 */
export function setSentryUser(user: User | null): void {
  if (!user) {
    // Clear user context on logout
    Sentry.setUser(null);
    return;
  }

  // Set user context with minimal PII
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.displayName || undefined,
  });

  // Add organization context if available
  if (user.organizationIds && user.organizationIds.length > 0) {
    Sentry.setTag('organization_count', user.organizationIds.length);
    Sentry.setTag('primary_organization', user.organizationIds[0]);
  }

  // Add role context
  if (user.role) {
    Sentry.setTag('user_role', user.role);
  }
}

/**
 * Capture a custom error to Sentry with additional context
 * 
 * @param error - The error to capture
 * @param context - Additional context to include with the error
 */
export function captureError(
  error: Error,
  context?: {
    tags?: Record<string, string>;
    level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
    extra?: Record<string, unknown>;
  }
): void {
  Sentry.captureException(error, {
    level: context?.level || 'error',
    tags: context?.tags,
    extra: context?.extra,
  });
}

/**
 * Add breadcrumb to Sentry for debugging
 * Breadcrumbs are events that lead up to an error
 * 
 * @param message - Description of what happened
 * @param category - Category of the breadcrumb (e.g., 'auth', 'navigation')
 * @param level - Severity level
 */
export function addBreadcrumb(
  message: string,
  category: string = 'custom',
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info'
): void {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    timestamp: Date.now() / 1000,
  });
}


